import { memo, useRef } from 'react';
import {
  PurchaseInvoiceModel,
  PurchaseInvoiceMonthsInformation,
  LedgerAccountEvent,
  Money,
} from 'models';
import { useTranslation } from 'react-i18next';

import { toDate, getMonthsList, formatNum } from 'utils';

import { PDFViewer, type PDFViewerRef } from 'features/pdf-viewer';

import PrintIcon from '@material-ui/icons/Print';
import GetAppIcon from '@material-ui/icons/GetApp';
import Button from '@material-ui/core/Button';
import { PageLoader, Error, BorderedBox, NoData } from 'components';

import { useStyles } from './style';

const monthsList = getMonthsList();

function getCurrentYear(
  purchaseInvoiceMonths: PurchaseInvoiceMonthsInformation | null
) {
  if (!purchaseInvoiceMonths) {
    return null;
  }

  const startDate = toDate(purchaseInvoiceMonths.FiscalYearStartDate);
  const endDate = toDate(purchaseInvoiceMonths.FiscalYearEndDate);

  if (!startDate || !endDate) {
    return null;
  }

  const startDateYear = startDate.getFullYear();
  const endDateYear = endDate.getFullYear();

  return startDateYear === endDateYear
    ? startDateYear
    : `${startDateYear} - ${endDateYear}`;
}

function extractMonthAmount(data: Money | null | undefined) {
  if (typeof data === 'object' && data !== null) {
    return `${formatNum(data.Value)} ${data.Symbol}`;
  }

  return formatNum(0);
}

interface Props {
  purchaseInvoice: PurchaseInvoiceModel | null;
  purchaseInvoiceMonths: PurchaseInvoiceMonthsInformation | null;
  ledgerAccountEvents: LedgerAccountEvent[];
  loading: boolean;
  error: boolean;
}

function extractLedgerAccountAmount(ledgerAccount: LedgerAccountEvent) {
  if (ledgerAccount.LedgerAccountCredit) {
    return `- ${formatNum(ledgerAccount.LedgerAccountCredit.Value)} ${
      ledgerAccount.LedgerAccountCredit.Symbol
    }`;
  } else if (ledgerAccount.LedgerAccountDebet) {
    return `${formatNum(ledgerAccount.LedgerAccountDebet.Value)} ${
      ledgerAccount.LedgerAccountDebet.Symbol
    }`;
  } else {
    return null;
  }
}

const PurchaseInvoiceDetails: React.FC<Props> = ({
  purchaseInvoice,
  purchaseInvoiceMonths,
  ledgerAccountEvents,
  loading,
  error,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();

  const pdfViewerRef = useRef<PDFViewerRef>(null);

  const handlePrintInvoice = () => {
    pdfViewerRef.current?.print();
  };

  const handleDownloadInvoice = () => {
    pdfViewerRef.current?.download(`Invoice-${purchaseInvoice?.Id}`);
  };

  if (loading) {
    return <PageLoader />;
  }

  if (error) {
    return <Error />;
  }

  if (!purchaseInvoice) {
    return <NoData />;
  }

  const currentYear = getCurrentYear(purchaseInvoiceMonths);
  const noData =
    !purchaseInvoiceMonths?.SellerToTotalPropertyMaintenanceCostsСorrelationPersent ||
    !purchaseInvoiceMonths?.TotalPropertyMaintenanceCosts;

  return (
    <div>
      <div className={classes.topBar}>
        <h2 className={classes.title}>
          {purchaseInvoice?.SellerOrganisationName} &mdash;{' '}
          {t('#portal.purchaseinvoice.grid.sellerinvoicenumber')}{' '}
          {purchaseInvoice?.SellerInvoiceNumber} &mdash;{' '}
          {formatNum(purchaseInvoiceMonths?.SellerTotalAmount?.Value)}{' '}
          {purchaseInvoiceMonths?.SellerTotalAmount?.Symbol}
        </h2>
        <div className={classes.controlBtns}>
          <Button
            onClick={handlePrintInvoice}
            classes={{ root: classes.btn }}
            disabled={!!pdfViewerRef.current?.disabled}
          >
            <PrintIcon className={classes.icon} />
          </Button>
          <Button
            disabled={!!pdfViewerRef.current?.disabled}
            onClick={handleDownloadInvoice}
            classes={{ root: classes.btn }}
          >
            <GetAppIcon className={classes.icon} />
          </Button>
        </div>
      </div>
      <div className={classes.container}>
        <div className={classes.leftBox}>
          <BorderedBox className={classes.borderedBox}>
            <h4 className={classes.boxTitle}>
              {purchaseInvoice?.SellerOrganisationName}
            </h4>
            <h4 className={classes.boxTitle}>
              {t('#portal.purchaseinvoice.detail.invoice')} {currentYear}
            </h4>
            <ul className={classes.monthsList}>
              {purchaseInvoiceMonths?.Months.map((month) => {
                const { TotalAmount } = month;

                return (
                  <li key={month.Month} className={classes.monthsListItem}>
                    {month.Month ? (
                      <span className={classes.month}>
                        {monthsList[month.Month - 1]}
                      </span>
                    ) : null}
                    <span className={classes.amount}>
                      {extractMonthAmount(TotalAmount)}
                    </span>
                  </li>
                );
              })}
              <li className={classes.divider}></li>
              <li className={classes.monthsListItem}>
                <span className={classes.month}>Yhteensä</span>
                <span className={classes.amount}>
                  {extractMonthAmount(purchaseInvoiceMonths?.SellerTotalAmount)}
                </span>
              </li>
            </ul>
            <h4 className={classes.boxTitle}>
              {purchaseInvoice?.SellerOrganisationName}
            </h4>
            <h4 className={classes.boxTitle}>
              {t('#portal.purchaseinvoice.detail.expenditurecomparison')}{' '}
              {currentYear}
            </h4>
            <p className={classes.greyText}>
              {t(
                '#portal.purchaseinvoice.detail.shareoftotalpropertymaintenancecosts'
              )}
            </p>
            <div className={classes.dividedBlock}>
              <div className={classes.dividedBlockItem}>
                {noData ? (
                  <p className={classes.dividedBlockError}>
                    {t('NoInformation')}
                  </p>
                ) : (
                  <>
                    <span className={classes.numerator}>
                      {formatNum(
                        purchaseInvoiceMonths?.SellerTotalAmount?.Value
                      )}{' '}
                      {purchaseInvoiceMonths?.SellerTotalAmount?.Symbol}
                    </span>
                    <span className={classes.divider}></span>
                    <span className={classes.denominator}>
                      {formatNum(
                        purchaseInvoiceMonths?.TotalPropertyMaintenanceCosts
                          ?.Value
                      )}{' '}
                      {
                        purchaseInvoiceMonths?.TotalPropertyMaintenanceCosts
                          ?.Symbol
                      }
                    </span>
                  </>
                )}
              </div>
              <div className={classes.dividedBlockItem}>
                <span className={classes.percentage}>
                  {purchaseInvoiceMonths?.SellerToTotalPropertyMaintenanceCostsСorrelationPersent
                    ? `${formatNum(
                        purchaseInvoiceMonths.SellerToTotalPropertyMaintenanceCostsСorrelationPersent
                      )} %`
                    : null}
                </span>
              </div>
            </div>
          </BorderedBox>
          <BorderedBox className={classes.borderedBox}>
            <h4 className={classes.boxTitle}>
              {t('#portal.purchaseinvoice.detail.ledgeraccountevents')}
            </h4>
            <ul className={classes.ledgerAccounts}>
              {ledgerAccountEvents.map((laEvent) => {
                return (
                  <li key={laEvent.Id} className={classes.ledgerAccount}>
                    <span className={classes.ledgerAccountText}>
                      {laEvent.LedgerAccountNumber}
                    </span>
                    <span className={classes.ledgerAccountText}>
                      {laEvent.LedgerAccount?.Name}
                    </span>
                    <span className={classes.ledgerAccountText}>
                      {extractLedgerAccountAmount(laEvent)}
                    </span>
                  </li>
                );
              })}
            </ul>
          </BorderedBox>
        </div>
        <div className={classes.rightBox}>
          <div className={classes.borderedBox}>
            {purchaseInvoice.ImageLink ? (
              <PDFViewer ref={pdfViewerRef} url={purchaseInvoice.ImageLink} />
            ) : (
              <NoData />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default memo(PurchaseInvoiceDetails);
